/**
 * OversightLevelSelector — per-run oversight level selector for the
 * pre-launch run-start surface.
 *
 * One compact row: the label on the left, the dropdown on the right. The `?`
 * tooltip lists every level so the AM can pick without opening the menu.
 *
 * Controlled — selection state is owned by the parent (the add-in drawer, or
 * ora's triage route) so the launch handler can read the current value. No
 * network calls (unlike OversightLevelSetting, which PUTs) — this is per-run
 * and ephemeral.
 *
 * PROMOTED to common/ on 2026-09-22. It lived under the add-in, which is why
 * ora shipped without the control at all: every import here was ALREADY from
 * the shared tree, so nothing about it was add-in-specific except its folder.
 * The launch-surface parity guard allowlisted the resulting difference rather
 * than catching it — see the note deleted from LAUNCH_SURFACE_ALLOWLIST.
 */
import SelectMenu from "~/common/components/SelectMenu";
import FieldHelpTooltip from "~/common/workflows/launch/FieldHelpTooltip";
import {
  OVERSIGHT_LEVEL_HELP,
  OVERSIGHT_LEVEL_OPTIONS,
  type OversightLevel,
} from "~/common/workflows/oversightLevels";

export interface OversightLevelSelectorProps {
  value: OversightLevel;
  onChange: (v: OversightLevel) => void;
  disabled?: boolean;
}

export function OversightLevelSelector({
  value,
  onChange,
  disabled,
}: OversightLevelSelectorProps) {
  return (
    <div
      className="flex items-center justify-between gap-2"
      data-testid="oversight-level-selector"
    >
      <label
        htmlFor="oversight-level-select"
        className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-primary-gray-light-700"
      >
        Oversight Level
        <FieldHelpTooltip
          text={OVERSIGHT_LEVEL_HELP}
          testId="oversight-level-help"
        />
      </label>
      {/* SelectMenu, not a native <select>: the browser draws a native
          option list's hover bar with its own highlight colour and ignores
          option:hover, so this dropdown stayed dark beside the light form
          however its options were styled. The rows here are real elements
          on the same ramp as OldMortgageeSelect. */}
      <SelectMenu
        id="oversight-level-select"
        label="Oversight Level"
        value={value}
        disabled={disabled}
        onChange={(next) => onChange(next as OversightLevel)}
        options={OVERSIGHT_LEVEL_OPTIONS.map((opt) => ({
          value: opt.value,
          label: opt.label,
          title: opt.description,
        }))}
        // A FIXED width, not a percentage cap. The trigger sizes to the
        // CURRENT value, so at "Low" it collapsed to about 70px and the panel
        // — which is w-full of this wrapper — truncated "Medium" and
        // "None" (reported 2026-09-22). The old max-w-[58%] only ever helped
        // in the add-in's narrow pane, where the label wrapped and left the
        // select room; in ora's wide row it constrains nothing. 9rem fits the
        // longest label plus the caret with margin to spare.
        className="w-36 shrink-0"
        data-testid="oversight-level-select"
        // Keeps the ids the existing tests query — same rows, new markup.
        optionTestIdPrefix="oversight-option"
      />
    </div>
  );
}

export default OversightLevelSelector;
